import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPasswordConfirm() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (!token) setError("Link inválido"); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error("Senha muito curta (mín 6 caracteres)");
    if (newPwd !== confirm) return toast.error("Confirmação não bate");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: newPwd });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2200);
    } catch (e) {
      setError(e.response?.data?.detail || "Erro ao redefinir");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Nova senha</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Redefinir senha</h1>

        {error && !done && (
          <div className="mt-8 bg-white border border-red-200 rounded-2xl p-6 text-center" data-testid="pwd-reset-error">
            <XCircle className="mx-auto text-red-600" size={40} />
            <div className="mt-4 font-heading font-black text-lg">{error}</div>
            <Link to="/auth/esqueci-senha" className="mt-4 inline-block text-sm underline font-semibold">Solicitar novo link</Link>
          </div>
        )}

        {!error && !done && (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="pwd-reset-confirm-form">
            <div>
              <Label>Nova senha</Label>
              <PasswordInput value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5 h-12" minLength={6} data-testid="reset-new-password" required />
            </div>
            <div>
              <Label>Confirmar</Label>
              <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-12" minLength={6} data-testid="reset-confirm-password" required />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-md bg-neutral-900 hover:bg-neutral-800" data-testid="reset-confirm-submit">
              <KeyRound size={16} className="mr-1.5"/> {loading ? "Salvando…" : "Redefinir senha"}
            </Button>
          </form>
        )}

        {done && (
          <div className="mt-8 bg-white border border-green-200 rounded-2xl p-6 text-center" data-testid="pwd-reset-success">
            <CheckCircle2 className="mx-auto text-green-600" size={40} />
            <div className="mt-4 font-heading font-black text-lg">Senha redefinida!</div>
            <p className="mt-2 text-sm text-neutral-600">Redirecionando ao login…</p>
          </div>
        )}
      </div>
    </div>
  );
}
